import React from 'react';
import { reduxForm } from 'redux-form';
import objectAssign from 'object-assign';
import classNames from 'classnames';
import values from 'lodash/object/values';

import { Body, Wrapper, Loading } from 'components/Component';
import Suggest from 'components/Suggest';
import SecurityDigest from '../SecurityDigest';
import ConfirmModal from 'components/Trade/Fragments/SellFragment/ConfirmModal';
import ReceiptModal from 'components/Trade/Fragments/ReceiptModal';
import TradeComponent from 'components/Trade';
import CurrencyConverter from 'components/CurrencyConverter';
import { getCurrency } from 'state/exchange/selectors';
import { getCurrency as getCurrencyData } from 'utils/currencies';

import * as FormUtils from 'utils/form';
import * as Trade from 'utils/trade';
import { getSelectedAccount, getCustomerNumber } from 'utils/customer';
import { getCustomerData } from 'utils/user';
import {
  errorsContain,
  getDetails,
  getErrorParamsFromError,
} from 'utils/errorMessage';
import validate from 'utils/validation/sellForm';

class SellFragment extends TradeComponent {
  state = {
    modalOpen: {},
    currencyExchangeRate: {},
    quickTrade: true,
    isFetching: false,
  };

  Instrument = React.createRef();

  constructor(props) {
    super(props);
    this.submit = this.submit.bind(this);
    this.checkFatFinger = this.checkFatFinger.bind(this);
  }

  getCurrencyExchangeRate = async () => {
    const { data } = await getCurrencyData(this.props.currency);
    this.setState(currentState => ({
      ...currentState,
      currencyExchangeRate: data,
    }));
  };

  componentDidUpdate(prevProps) {
    if (
      prevProps.currency !== this.props.currency &&
      this.props.currency !== 'NOK'
    ) {
      this.getCurrencyExchangeRate();
    }
  }

  componentDidMount() {
    if (this.props.currency !== 'NOK') {
      this.getCurrencyExchangeRate();
    }
  }

  componentWillUnmount() {
    this.props.expandToFullWidth(false);
  }

  getErrors(err, res) {
    const errors = { _error: '' };

    if (errorsContain('LIMIT_ROUNDED', res.text)) {
      errors.limit = 'Limit avrundet.';
      const params = getErrorParamsFromError('LIMIT_ROUNDED', res.text);
      this.props.fields.limit.onChange(params.LIMIT.toString());
    }

    if (errorsContain('STOP_LOSS_LIMIT_ROUNDED', res.text)) {
      errors.stopLoss = 'Stop Loss avrundet.';
      const params = getErrorParamsFromError(
        'STOP_LOSS_LIMIT_ROUNDED',
        res.text,
      );
      this.props.fields.stopLoss.onChange(params.STOP_LOSS_LIMIT.toString());
    }

    if (errorsContain('NO_COVERAGE', res.text)) {
      errors._error +=
        'Du har ikke dekning på konto for å gjennomføre salget.' +
        ' dersom det er avvik i beholdning kan du registrere%LINK_MAN_TRANS%';
    }

    if (errorsContain('COVERAGE', res.text)) {
      errors._error += 'Du har ikke dekning på konto for å gjennomføre salget.';
    }

    if (errorsContain('No coverage', res.text)) {
      errors._error +=
        'Du har ikke dekning på konto for å gjennomføre salget.' +
        ' dersom det er avvik i beholdning kan du registrere%LINK_MAN_TRANS%';
    }

    if (errorsContain('CANNOT_TRADE_INSTRUMENT', res.text)) {
      errors._error +=
        'Du har ikke godkjenning for å gjennomføre ' +
        'salg i dette instrumentet.';
    }

    if (errorsContain('No coverage: No coverage', res.text)) {
      errors._error += 'Du har ikke dekning på konto for å gjennomføre salget.';
    }

    if (errorsContain('EXPIRATION_DATE', res.text)) {
      errors._error += 'Ugyldig dato.';
    }

    const coverage = res?.body?.detail;
    if (coverage?.includes('No coverage: No coverage')) {
      errors._error += 'Du har ikke dekning på konto for å gjennomføre salget.';
    }

    // If no known validations matched
    if (errors._error.length === 0 && !errors.limit && !errors.stopLoss) {
      errors._error +=
        'En feil oppsto ved kontroll av ordren. ' +
        getDetails(res.text).join(', ');
    }

    return errors;
  }

  submit(vals) {
    const { instrument, volume, limit } = vals;

    return new Promise((resolve, reject) => {
      const { user } = this.props;
      const selectedAccount = getSelectedAccount(getCustomerData(user));
      const normalizedLimit = FormUtils.normalizeNumber(limit);

      this.setState({
        isFetching: true,
      });

      const instrumentAccount =
        instrument.SECTOR === 'SSE' ? 'NO_ACCOUNT' : null;

      const payload = {
        tradeType: 'SELL',
        ticker: instrument.ITEM,
        exchange: instrument.SECTOR,
        volume: FormUtils.normalizeNumber(volume),
        useOpenVolume: false,
        openVolume: null,
        instrumentAccount,
        limit: normalizedLimit,
        expirationDate: null,
        customerId: selectedAccount.get('customerId'),
        currencyExchangeRate:
          this.props.currency !== 'NOK'
            ? this.state.currencyExchangeRate.ask
            : undefined,
      };

      const callback = (err, res) => {
        this.setState({ isFetching: false });
        if (err) {
          reject(this.getErrors(err, res));
          return;
        }

        this.setState({ validated: res.body.data, payload }, () => {
          this.toggleModal('confirmSell');
        });

        resolve();
      };

      Trade.validate(getCustomerNumber(user), payload, callback);
    });
  }

  handleConfirmSubmit() {
    this.setState({ executeError: null, transactionInTransit: true });

    const { user } = this.props;

    let payload = this.state.payload;

    Trade.execute(getCustomerNumber(user), payload, (err, res) => {
      // Handle errors from ProBroker
      if (err) {
        this.toggleModal('confirmSell');
        this.setState({
          executeErrors: this.getErrors(err, res),
          transactionInTransit: false,
        });
      } else {
        // Wait a little bit to prevent getting old data when updating
        setTimeout(() => {
          // Get more details about this order
          this.getOrderDetails(res.body.data.ORDER_ID);

          this.setState(
            {
              transactionInTransit: false,
              completeOrder: objectAssign(
                {},
                this.state.validated,
                res.body.data,
              ),
            },
            () => this.toggleModal('confirmSell'),
          );
        }, 1000);
      }
    });
  }

  toggleModal(id, e) {
    if (e && e.preventDefault) e.preventDefault();

    const { modalOpen } = this.state;
    modalOpen[id] = !modalOpen[id];
    this.setState({ modalOpen });
  }

  render() {
    const {
      fields: { instrument, limit, volume },
      error,
      errors,
      submitFailed,
      handleSubmit,
      currency,
      clearTradeForm,
    } = this.props;

    const {
      executeErrors,
      completeOrder,
      validated,
      transactionInTransit,
      warnings,
      quickTrade,
      isFetching,
      currencyExchangeRate,
    } = this.state;

    return (
      <Wrapper
        className={classNames('SellFragment', {
          small: !instrument.value,
        })}
      >
        <Body>
          <form onSubmit={handleSubmit(this.submit)}>
            <section
              className={classNames('Instrument', {
                invalid: instrument.touched && errors.instrument,
              })}
            >
              <label htmlFor="SellInstrument">Selskap/Ticker</label>
              <Suggest
                id="SellInstrument"
                innerRef={this.Instrument}
                tradable={true}
                onSelect={newVal => {
                  const itemSector = `${newVal.ITEM}.${newVal.SECTOR}`;
                  this.loadFromItemSector(itemSector);
                  this.props.expandToFullWidth(true);
                }}
              />
            </section>

            <section
              className={classNames({
                invalid: limit.touched && errors.limit,
              })}
            >
              <label htmlFor="Limit">Limit/Pris</label>
              <div className={`currency-${currency}`}>
                <input
                  id="Limit"
                  {...limit}
                  onChange={this.checkFatFinger}
                  onBlur={this.checkFatFinger}
                />
              </div>
              {currency !== 'NOK' && (
                <CurrencyConverter
                  id="currency"
                  tradeType="SELL"
                  quickTrade={quickTrade}
                  value={limit.value}
                  currency={currency}
                  currencyExchangeRate={currencyExchangeRate}
                />
              )}
            </section>

            <section
              className={classNames({
                invalid: volume.touched && errors.volume,
              })}
            >
              <label htmlFor="Volume">Volum/Antall</label>
              <input id="Volume" {...volume} />
            </section>

            <section className="buttons">
              <input
                type="submit"
                value="Selg"
                onClick={() =>
                  this.reportInvalidFormIfInvalid(
                    'QuickTrade SellFragment validation failed',
                  )
                }
              />
            </section>

            {warnings && <span className="flash-alert errors">{warnings}</span>}

            {!!instrument.value && executeErrors && (
              <span className="flash-alert errors">
                {this.printError(executeErrors._error, instrument.value)}
              </span>
            )}

            {submitFailed && !!values(errors).length && (
              <section className="flash-error errors">
                {values(errors).map(e => (
                  <p>{e}</p>
                ))}
              </section>
            )}

            {error && (
              <span className="flash-error errors">
                {this.printError(error, instrument.value)}
              </span>
            )}
          </form>
          {!!instrument.value && <SecurityDigest item={instrument.value} />}
        </Body>
        {isFetching && <Loading />}
        {!!this.state.modalOpen.confirmSell && (
          <ConfirmModal
            currency={currency}
            order={validated}
            handleHideModal={() => this.toggleModal('confirmSell')}
            handleConfirmSubmit={e => this.handleConfirmSubmit(e)}
            transactionInTransit={transactionInTransit}
            errors={this.state.errors}
          />
        )}
        {!!completeOrder && (
          <ReceiptModal
            order={completeOrder}
            handleHideModal={() => {
              this.setState({ completeOrder: undefined });
              clearTradeForm();
            }}
          />
        )}
      </Wrapper>
    );
  }
}

const fields = [
  // some values that are not part of the payload must be part of
  // the redux form, because the validate and formatters do not
  // have access to the component local state
  'peakSize',
  'tickSizeRules',
  'lotSizeRule',
  'last',
  'instrument',
  'accountId',
  'limit',
  'volume',
];

const reduxFormOpts = {
  form: 'sellComponent',
  fields,
  validate,
};

function mapStateToProps(state) {
  const { user } = state;
  const customerData = getCustomerData(user);
  const accounts = customerData.get('accounts').map(account => {
    return {
      value: account.get('id'),
      label: account.get('fullName'),
    };
  });

  return {
    user,
    currency: getCurrency(state),
    initialValues: {
      accountId: accounts.first() && accounts.first().value,
      limit: undefined,
      volume: undefined,
    },
  };
}

export { SellFragment };
export default reduxForm(reduxFormOpts, mapStateToProps)(SellFragment);
